import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { REVIEWER_REVISION_CONFLICT_MESSAGE, revisionConflictFromHttp } from '../../core/http/revision-conflict';
import { ReviewerAcceptedCase, ReviewerAssignmentSummary, ReviewerCaseView, ReviewerDeterminationDraft, ReviewerMutationResult } from './reviewer.models';
import { ReviewerCaseService } from './reviewer-case.service';

@Injectable({ providedIn: 'root' })
export class HttpReviewerCaseService implements ReviewerCaseService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/reviewer-cases`;

  listAssignments(): Observable<readonly ReviewerAssignmentSummary[]> {
    return this.http.get<readonly ReviewerAssignmentSummary[]>(`${this.baseUrl}/assignments`);
  }

  getCase(caseNumber: string): Observable<ReviewerCaseView | null> {
    return this.http
      .get<ReviewerCaseView>(this.caseUrl(caseNumber))
      .pipe(catchError((error: unknown) => (error instanceof HttpErrorResponse && error.status === 404 ? of(null) : throwError(() => error))));
  }

  acceptAssignment(caseNumber: string, attestation: boolean, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerCaseView>> {
    return this.http.post<ReviewerMutationResult<ReviewerCaseView>>(`${this.caseUrl(caseNumber)}/acceptance`, { attestation, expectedRevision }).pipe(this.mutationError<ReviewerCaseView>());
  }

  declineAssignment(caseNumber: string, reason: string, explanation: string, expectedRevision: number): Observable<ReviewerMutationResult<null>> {
    return this.http.post<ReviewerMutationResult<null>>(`${this.caseUrl(caseNumber)}/decline`, { reason, explanation, expectedRevision }).pipe(this.mutationError<null>());
  }

  saveDeterminationDraft(caseNumber: string, draft: ReviewerDeterminationDraft, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>> {
    return this.http.put<ReviewerMutationResult<ReviewerAcceptedCase>>(`${this.caseUrl(caseNumber)}/determination-draft`, { draft, expectedRevision }).pipe(this.mutationError<ReviewerAcceptedCase>());
  }

  markPreviewed(caseNumber: string, expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>> {
    return this.http.post<ReviewerMutationResult<ReviewerAcceptedCase>>(`${this.caseUrl(caseNumber)}/determination-preview`, { expectedRevision }).pipe(this.mutationError<ReviewerAcceptedCase>());
  }

  submitDetermination(caseNumber: string, typedName: string, attestations: readonly boolean[], expectedRevision: number): Observable<ReviewerMutationResult<ReviewerAcceptedCase>> {
    return this.http
      .post<ReviewerMutationResult<ReviewerAcceptedCase>>(`${this.caseUrl(caseNumber)}/determination-submission`, { typedName, attestations, expectedRevision })
      .pipe(this.mutationError<ReviewerAcceptedCase>());
  }

  openDocument(caseNumber: string, documentId: string): Observable<ReviewerMutationResult<Blob>> {
    return this.http.get(`${this.caseUrl(caseNumber)}/documents/${encodeURIComponent(documentId)}`, { responseType: 'blob' }).pipe(
      map((blob) => ({ ok: true, value: blob }) as ReviewerMutationResult<Blob>),
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === 404 ? of({ ok: false, message: 'That document is not available.' } as ReviewerMutationResult<Blob>) : throwError(() => error),
      ),
    );
  }

  private caseUrl(caseNumber: string): string {
    return `${this.baseUrl}/${encodeURIComponent(caseNumber)}`;
  }

  private mutationError<T>() {
    return (source: Observable<ReviewerMutationResult<T>>): Observable<ReviewerMutationResult<T>> =>
      source.pipe(
        catchError((error: unknown) => {
          const conflict = revisionConflictFromHttp(error, REVIEWER_REVISION_CONFLICT_MESSAGE);
          return conflict ? throwError(() => conflict) : of({ ok: false, message: this.errorMessage(error, 'The action could not be completed.') });
        }),
      );
  }

  private errorMessage(error: unknown, fallback: string): string {
    if (!(error instanceof HttpErrorResponse)) return fallback;
    const detail = error.error && typeof error.error === 'object' && 'detail' in error.error ? error.error.detail : null;
    return typeof detail === 'string' && detail.trim() ? detail : fallback;
  }
}
